import { Injectable } from '@angular/core';
import { ModelPage, ModelService, PagedModelService } from '@shared/model-system';
import { ApiService } from '@shared/services/api.service';
import { extend } from 'lodash';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Division } from './division';

/** Division service using the model system */
@Injectable()
export class DivisionService extends ModelService<Division> implements PagedModelService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Type = Division;
  private readonly baseURI = '/platform/api/v2/authorization/divisions/';

  constructor(public api: ApiService) {
    super();
  }

  public get(id: string): Observable<Division> {
    return this.api.get(this.baseURI + id).pipe(map((data) => this.create(data)));
  }

  public getPage({
    pageNumber = 1,
    pageSize = 100,
    params = ''
  }: IDivisionServiceGetPageOptions): Observable<ModelPage<Division>> {
    const extendedParams = extend({}, { pageNumber, pageSize }, params);
    let total = 0;
    let pageCount = 0;

    return this.api.get(this.baseURI, extendedParams).pipe(
      tap((data) => {
        total = data.total;
        pageCount = data.pageCount;
      }),
      map((data) => data.entities.map((entity) => this.create(entity))),
      map((divisions: Division[]) => ({
        entities: divisions,
        total,
        pageNumber,
        pageSize,
        pageCount
      }))
    );
  }

  public getHome(): Observable<Division> {
    return this.api.get(`${this.baseURI}home`).pipe(map((data) => this.create(data)));
  }

  public assignObjects(id: string, objectType: string, objectIds: string[]): Observable<any> {
    return this.api.post(`${this.baseURI}${id}/objects/${objectType}`, objectIds);
  }

  public getPermittedDivisionsByPermission(
    permission: string,
    pageNumber: number,
    pageSize: number
  ): Observable<ModelPage<Division>> {
    let total = 0;
    let pageCount = 0;

    return this.api
      .get('/platform/api/v2/authorization/divisionspermitted/paged/me', {
        permission,
        pageNumber,
        pageSize
      })
      .pipe(
        tap((data) => {
          total = data.total;
          pageCount = data.pageCount;
        }),
        map((data) => data.entities.map((entity) => this.create(entity))),
        map((divisions: Division[]) => ({
          entities: divisions,
          total,
          pageNumber,
          pageSize,
          pageCount
        }))
      );
  }
}

interface IDivisionServiceGetPageOptions {
  pageNumber?: number;
  pageSize?: number;
  params?: any;
}
